import { surfaceBase } from "@web/components/ui";
import { useState } from "react";
import { disconnectGoogleHealth } from "@/features/sync/google-health";
import type { ProviderConnection } from "@/types";

const APP_STORE_URL = "https://apps.apple.com/app/id6781928157";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.footslapping";

function formatLastSync(lastSyncAt: string | null): string {
  if (!lastSyncAt) return "Never";
  const d = new Date(lastSyncAt);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

interface Props {
  connections: ProviderConnection[];
}

export default function HealthConnectionsSection({ connections }: Props) {
  const [disconnecting, setDisconnecting] = useState(false);
  const [googleConn, setGoogleConn] = useState(
    () => connections.find((c) => c.provider === "google_health") ?? null,
  );

  const mobileOnlyProviders = [
    {
      key: "apple_health",
      label: "Apple Health",
      description: "Syncs your step data if you use an iPhone.",
    },
    {
      key: "health_connect",
      label: "Health Connect",
      description: "Syncs your step data on Android — works with Fitbit and Samsung Health too.",
    },
  ];

  async function handleDisconnectGoogle() {
    setDisconnecting(true);
    const result = await disconnectGoogleHealth();
    setDisconnecting(false);
    if (result.success) {
      setGoogleConn((prev) => (prev ? { ...prev, status: "not_connected" } : prev));
    }
  }

  const isGoogleConnected = googleConn?.status === "connected";

  return (
    <div id="health-connections" className={`${surfaceBase} bg-card p-5`}>
      <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
        Health Connections
      </span>
      <p className="text-text-muted text-[0.8125rem] mt-1 leading-[1.125]">
        Step syncing now happens only through the FootSlapping iOS and Android apps — Apple Health
        or Health Connect. Download the app to connect a step source.
      </p>

      {isGoogleConnected && (
        <div className="mt-4 rounded-[16px] border border-amber bg-amber-bg p-4">
          <p className="text-text text-[0.875rem] font-bold">
            Action needed: Google Health sync is going away
          </p>
          <p className="text-text-muted text-[0.8125rem] mt-1 leading-[1.125]">
            We&apos;ve discontinued Google Health step sync. Download the app below and connect
            Apple Health or Health Connect to keep syncing, then disconnect Google Health here.
          </p>
          <p className="text-text-dim text-[0.75rem] mt-2">
            Last synced: {formatLastSync(googleConn?.lastSyncAt ?? null)}
          </p>
          <button
            type="button"
            onClick={handleDisconnectGoogle}
            disabled={disconnecting}
            className="mt-3 bg-[rgba(28,27,46,0.05)] rounded-[12px] px-3 py-1.5 text-text text-[0.75rem] font-bold border border-[rgba(28,27,46,0.1)] cursor-pointer hover:bg-[rgba(28,27,46,0.1)] transition-colors disabled:opacity-50"
          >
            {disconnecting ? "Disconnecting..." : "Disconnect Google Health"}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-4">
        {mobileOnlyProviders.map((provider) => {
          const conn = connections.find((c) => c.provider === provider.key);
          const isConnected = conn?.status === "connected";

          return (
            <div
              key={provider.key}
              className="flex items-center justify-between py-3 px-4 rounded-[16px] border border-[rgba(28,27,46,0.1)]"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-text text-[0.875rem] font-bold">{provider.label}</span>
                <span className="text-text-dim text-[0.75rem]">
                  {isConnected ? `Synced ${formatLastSync(conn.lastSyncAt)}` : provider.description}
                </span>
              </div>
              <span className="text-text-dim text-[0.75rem] font-bold px-3 py-1.5 rounded-[12px] bg-[rgba(28,27,46,0.05)]">
                {isConnected ? "Connected" : "Requires mobile app"}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-row gap-3 mt-4">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-aqua rounded-2xl py-3 px-5 items-center justify-center shadow-[1px_1px_0px_0px_#1c1b2e] border border-text no-underline inline-flex font-inherit cursor-pointer"
        >
          <span className="text-white font-extrabold text-sm">Get the iOS app</span>
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-aqua rounded-2xl py-3 px-5 items-center justify-center shadow-[1px_1px_0px_0px_#1c1b2e] border border-text no-underline inline-flex font-inherit cursor-pointer"
        >
          <span className="text-white font-extrabold text-sm">Get the Android app</span>
        </a>
      </div>
    </div>
  );
}
